import { useSelector } from 'react-redux';
import { useRefs } from './RefContext';
import { RootState } from 'store';
import { createContext, MutableRefObject, useContext, useMemo } from 'react';
import { STANDARD_IMAGERY_SIZE } from '../constants';
import useKonvaStageDimensions from 'hooks/konva/useKonvaStageDims';

interface ToolDimensionsAndScaleContextProps {
	scale: { x: number; y: number };
	imageDimensions: Dimensions;
	konvaStageDims: Dimensions;
}

interface ToolDimensionsAndScaleProviderProps {
	children: React.ReactNode,
	toolContainerRef: MutableRefObject<HTMLDivElement | null>
}

const ToolDimensionsAndScaleContext = createContext<ToolDimensionsAndScaleContextProps | null>(null);

export default function ToolDimensionsAndScaleProvider({ children, toolContainerRef }: ToolDimensionsAndScaleProviderProps) {
	const { konvaRef } = useRefs();
	const { imgHeight, imgWidth, org } = useSelector((state: RootState) => state.roofData.data);
	const isInFullViewMode = useSelector((state: RootState)=> state.toolNewPostions.data.fullScreenEnabled);
	const konvaStageDims = useKonvaStageDimensions({ stageWrapRef: toolContainerRef });

	const restrictMinSize = !org.setting.isB2C;

	const { height: konvaStageHeight } = konvaStageDims;

	// The imagery always keeps its aspect ratio, B2B and B2C alike — width follows height, never the
	// canvas width, or it stretches on any canvas of a different shape (e.g. a 420px-wide phone).
	// For B2C, useKonvaPositioning then zooms it to cover the canvas.
	const imageDimensions = useMemo(() => {
		if (konvaRef?.current && !isInFullViewMode) {
			const aspectRatio = imgWidth / imgHeight;
			const height = (restrictMinSize && konvaStageHeight < STANDARD_IMAGERY_SIZE) ? STANDARD_IMAGERY_SIZE : konvaStageHeight;
			const width = height * aspectRatio;
			return { width, height };
		}
		return { width: STANDARD_IMAGERY_SIZE, height: STANDARD_IMAGERY_SIZE };
	}, [konvaRef, isInFullViewMode, imgWidth, imgHeight, restrictMinSize, konvaStageHeight]);

	const scale = useMemo(() => {
		let x = 1, y = 1;
		if (konvaRef?.current) {
			x = imageDimensions.width / imgWidth;
			y = imageDimensions.height / imgHeight;
		}
		return { x, y };
	}, [imgHeight, imgWidth, konvaRef, imageDimensions]);

	// a new object here on every render re-rendered every consumer (each panel, label and facet)
	const value = useMemo(() => ({ scale, imageDimensions, konvaStageDims }), [scale, imageDimensions, konvaStageDims]);

	return (
		<ToolDimensionsAndScaleContext.Provider value={value}>
			{children}
		</ToolDimensionsAndScaleContext.Provider>
	);
}

export const useKonvaImageScale = (): Vector2d => {
	const context = useContext(ToolDimensionsAndScaleContext);
	if (!context) {
		throw new Error('useKonvaImageScale must be used within a ToolDimensionsAndScaleProvider');
	}
	return context.scale;
};

export const useKonvaStageDims = (): Dimensions => {
	const context = useContext(ToolDimensionsAndScaleContext);
	if (!context) {
		throw new Error('useKonvaStageDims must be used within a ToolDimensionsAndScaleProvider');
	}
	return context.konvaStageDims;
};

export const useKonvaImageDimensions = (): Dimensions => {
	const context = useContext(ToolDimensionsAndScaleContext);
	if (!context) {
		throw new Error('useKonvaImageDimensions must be used within a ToolDimensionsAndScaleProvider');
	}
	return context.imageDimensions;
};
